import React, { useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import './Calendar.css'; 

const VINTAGE_BEIGE = "#e5d1a5"; 
const JERUSALEM_GOLD = "#c5a059"; 
const ACCENT_RED = "#8b0000"; 
const SUCCESS_BORDER = "#4caf50";

const formatDateForInput = (dateObj) => {
  if (!dateObj) return '';
  const offset = dateObj.getTimezoneOffset() * 60000;
  return new Date(dateObj - offset).toISOString().split('T')[0];
};

const formatTimeForInput = (dateObj) => {
  if (!dateObj) return '';
  return dateObj.toTimeString().slice(0, 5);
};

const Calendar = ({ player = {}, setPlayer, completeQuest, toggleHabit }) => {
  const calendarRef = useRef(null);
  
  const events = player.calendarEvents || [];

  const [modalState, setModalState] = useState({ 
    isOpen: false, 
    type: null, 
    eventData: null, 
    selectInfo: null,
    prefillData: null 
  });

  const handleEventDrop = (info) => {
    setPlayer(prev => ({
      ...prev,
      calendarEvents: (prev.calendarEvents || []).map(evt => 
        evt.id === info.event.id 
          ? { ...evt, start: info.event.start.toISOString(), end: info.event.end ? info.event.end.toISOString() : null } 
          : evt
      )
    }));
  };

  const handleEventResize = (info) => {
    setPlayer(prev => ({
      ...prev,
      calendarEvents: (prev.calendarEvents || []).map(evt => 
        evt.id === info.event.id 
          ? { ...evt, start: info.event.start.toISOString(), end: info.event.end ? info.event.end.toISOString() : null } 
          : evt
      )
    }));
  };

  const handleDateSelect = (selectInfo) => {
    const calendarApi = selectInfo.view.calendar;
    calendarApi.unselect(); 
    setModalState({ isOpen: true, type: 'add', selectInfo, eventData: null, prefillData: null });
  };

  const handleEventClick = (clickInfo) => {
    setModalState({ isOpen: true, type: 'view', eventData: clickInfo.event, selectInfo: null, prefillData: null });
  };

  const handleEventRemove = (eventId) => {
    if(window.confirm(`Are you certain you wish to remove this ritual from the timeline?`)) {
        setPlayer(prev => ({
          ...prev,
          calendarEvents: (prev.calendarEvents || []).filter(e => e.id !== eventId)
        }));
        closeModal();
    }
  };

  const handleCompleteTask = (e) => {
    const { sourceId, sourceType } = e.extendedProps;

    if (sourceType === 'activeQuest' && completeQuest) {
      completeQuest(sourceId);
    } else if (sourceType === 'habit' && toggleHabit) {
      toggleHabit(sourceId, true);
    }

    setPlayer(prev => ({
      ...prev,
      calendarEvents: (prev.calendarEvents || []).map(evt => 
        evt.id === e.id ? {
          ...evt,
          backgroundColor: 'rgba(46, 125, 50, 0.4)',
          borderColor: SUCCESS_BORDER,
          extendedProps: { ...evt.extendedProps, isCompleted: true }
        } : evt
      )
    }));

    closeModal();
  };

  const handleFormSubmit = (e) => {
      e.preventDefault();
      const title = document.getElementById('evt-title').value.trim();
      const desc = document.getElementById('evt-desc').value.trim();
      const date = document.getElementById('evt-date').value;
      const start = document.getElementById('evt-start').value;
      const end = document.getElementById('evt-end').value;

      const startTime = new Date(`${date}T${start}`);
      const endTime = new Date(`${date}T${end}`);

      if (modalState.type === 'add') {
        let sourceId = modalState.prefillData?.sourceId || null;
        let sourceType = modalState.prefillData?.sourceType || null;

        setPlayer(prev => {
          let updatedBacklog = prev.backlog || [];
          let updatedActiveQuests = prev.activeQuests || [];

          if (sourceType === 'backlog') {
            const newQuestId = Date.now() + Math.floor(Math.random() * 1000);
            const item = updatedBacklog.find(b => b.id === sourceId);
            if (item) {
              updatedBacklog = updatedBacklog.filter(b => b.id !== sourceId);
              updatedActiveQuests = [...updatedActiveQuests, { id: newQuestId, text: item.text, stat: 'discipline', priority: 'medium', val: 10 }];
              sourceId = newQuestId;
              sourceType = 'activeQuest';
            }
          }

          const newEvent = {
            id: `custom-${Date.now()}`,
            title,
            start: startTime.toISOString(),
            end: endTime.toISOString(),
            extendedProps: { description: desc, sourceId, sourceType, isCompleted: false },
            backgroundColor: 'rgba(0, 0, 0, 0.7)', 
            borderColor: JERUSALEM_GOLD,
            textColor: VINTAGE_BEIGE
          };

          return {
            ...prev,
            backlog: updatedBacklog,
            activeQuests: updatedActiveQuests,
            calendarEvents: [...(prev.calendarEvents || []), newEvent]
          };
        });

      } else if (modalState.type === 'edit') {
        setPlayer(prev => ({
          ...prev,
          calendarEvents: (prev.calendarEvents || []).map(evt => 
            evt.id === modalState.eventData.id 
              ? { ...evt, title, start: startTime.toISOString(), end: endTime.toISOString(), extendedProps: { ...evt.extendedProps, description: desc } } 
              : evt
          )
        }));
      }
      closeModal();
  };

  const closeModal = () => setModalState({ isOpen: false, type: null, eventData: null, selectInfo: null, prefillData: null });

  const openAddEventWithQuest = (title, description, sourceId, sourceType) => {
    setModalState({
      isOpen: true,
      type: 'add',
      selectInfo: null,
      eventData: null,
      prefillData: { title, description, sourceId, sourceType }
    });
  };

  const renderModalContent = () => {
    if (modalState.type === 'view') {
      const e = modalState.eventData;
      const isLinkedToSystem = !!e.extendedProps.sourceId; 
      const isCompleted = e.extendedProps.isCompleted; 

      return (
        <div style={{ color: VINTAGE_BEIGE, fontFamily: 'Courier Prime, monospace' }}>
          <p style={{ margin: '5px 0', fontSize: '1.1rem' }}>
            <strong style={{ color: JERUSALEM_GOLD }}>Time:</strong> {e.start.toLocaleString([], { hour: '2-digit', minute: '2-digit' })} — {e.end ? e.end.toLocaleString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
          </p>
          <p style={{ margin: '20px 0 5px 0' }}><strong style={{ color: JERUSALEM_GOLD }}>Directives:</strong></p>
          <p style={{ background: 'rgba(0,0,0,0.5)', padding: '15px', borderRadius: '4px', borderLeft: `3px solid ${isCompleted ? SUCCESS_BORDER : JERUSALEM_GOLD}`, margin: 0, minHeight: '60px' }}>
            {e.extendedProps.description || 'No specific directive set.'}
          </p>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '30px' }}>
             <div>
               {isLinkedToSystem && !isCompleted ? (
                 <button 
                    onClick={() => handleCompleteTask(e)}
                    style={{ background: 'rgba(46, 125, 50, 0.2)', border: `1px solid ${SUCCESS_BORDER}`, color: SUCCESS_BORDER, padding: '10px 20px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontWeight: 'bold', transition: '0.2s' }}
                 >
                    ✓ Complete Ritual
                 </button>
               ) : isCompleted ? (
                 <span style={{ color: SUCCESS_BORDER, fontFamily: 'Cinzel, serif', fontWeight: 'bold' }}>✓ PROTOCOL FULFILLED</span>
               ) : null}
             </div>

             <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                    onClick={() => setModalState({ ...modalState, type: 'edit' })}
                    style={{ background: 'transparent', border: `1px solid ${JERUSALEM_GOLD}`, color: JERUSALEM_GOLD, padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontWeight: 'bold', transition: '0.2s' }}
                >
                    Edit
                </button>
                <button 
                    onClick={() => handleEventRemove(e.id)}
                    style={{ background: 'rgba(139, 0, 0, 0.1)', border: `1px solid ${ACCENT_RED}`, color: '#ff6b6b', padding: '10px 15px', borderRadius: '4px', cursor: 'pointer', fontFamily: 'Cinzel, serif', fontWeight: 'bold', transition: '0.2s' }}
                >
                    Delete
                </button>
             </div>
          </div>
        </div>
      );
    }

    if (modalState.type === 'select-quest') {
      const activeQuests = player.activeQuests || [];
      const backlogQuests = player.backlog || [];
      const rituals = player.habits || [];
      const hasNoQuests = activeQuests.length === 0 && backlogQuests.length === 0 && rituals.length === 0;

      return (
        <div className="quest-selector-scroll" style={{ color: VINTAGE_BEIGE, fontFamily: 'Courier Prime, monospace', maxHeight: '55vh', overflowY: 'auto', paddingRight: '10px' }}>
          <p style={{ opacity: 0.7, marginBottom: '20px' }}>Select a pending directive to allocate time for it.</p>
          
          {hasNoQuests && (
            <div style={{ textAlign: 'center', padding: '30px', border: `1px dashed ${JERUSALEM_GOLD}`, opacity: 0.5 }}>
              No active quests found in the archives.
            </div>
          )}

          {activeQuests.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: JERUSALEM_GOLD, borderBottom: `1px solid ${JERUSALEM_GOLD}`, paddingBottom: '5px' }}>Active Objectives (Quest Board)</h4>
              {activeQuests.map(q => (
                <div key={q.id} className="quest-select-card" onClick={() => openAddEventWithQuest(q.text, `Attribute Focus: ${q.stat?.toUpperCase() || 'GENERAL'}`, q.id, 'activeQuest')}>
                  <span>{q.text}</span>
                  <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>+ {q.val} XP</span>
                </div>
              ))}
            </div>
          )}

          {backlogQuests.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ color: JERUSALEM_GOLD, borderBottom: `1px solid ${JERUSALEM_GOLD}`, paddingBottom: '5px' }}>Pending Missions (Planner)</h4>
              {backlogQuests.map(b => (
                <div key={b.id} className="quest-select-card" onClick={() => openAddEventWithQuest(b.text, 'From Planner Backlog. Will be activated upon scheduling.', b.id, 'backlog')}>
                  <span>{b.text}</span>
                </div>
              ))}
            </div>
          )}

          {rituals.length > 0 && (
            <div style={{ marginBottom: '10px' }}>
              <h4 style={{ color: JERUSALEM_GOLD, borderBottom: `1px solid ${JERUSALEM_GOLD}`, paddingBottom: '5px' }}>Daily Rituals</h4>
              {rituals.map(r => (
                <div key={r.id} className="quest-select-card" onClick={() => openAddEventWithQuest(r.text, 'Daily Routine Maintenance', r.id, 'habit')}>
                  <span>{r.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    let defaultTitle = '', defaultDesc = '', defaultDate = '', defaultStart = '', defaultEnd = '';
    
    if (modalState.type === 'edit' && modalState.eventData) {
      defaultTitle = modalState.eventData.title;
      defaultDesc = modalState.eventData.extendedProps.description;
      defaultDate = formatDateForInput(modalState.eventData.start);
      defaultStart = formatTimeForInput(modalState.eventData.start);
      defaultEnd = formatTimeForInput(modalState.eventData.end);
    } else if (modalState.type === 'add') {
      if (modalState.selectInfo) {
        defaultDate = formatDateForInput(modalState.selectInfo.start);
        defaultStart = formatTimeForInput(modalState.selectInfo.start);
        defaultEnd = formatTimeForInput(modalState.selectInfo.end);
      }
      if (modalState.prefillData) {
        defaultTitle = modalState.prefillData.title;
        defaultDesc = modalState.prefillData.description;
      }
    }

    return (
      <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: 'Courier Prime, monospace', color: VINTAGE_BEIGE }}>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', color: JERUSALEM_GOLD, fontWeight: 'bold' }}>Objective Title:</label>
          <input type="text" id="evt-title" defaultValue={defaultTitle} required autoFocus style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${JERUSALEM_GOLD}`, color: '#fff', borderRadius: '4px', outline: 'none', fontSize: '1.1rem' }} />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '8px', color: JERUSALEM_GOLD, fontWeight: 'bold' }}>Description / Directives:</label>
          <textarea id="evt-desc" rows="3" defaultValue={defaultDesc} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${JERUSALEM_GOLD}`, color: '#fff', borderRadius: '4px', outline: 'none', resize: 'none' }}></textarea>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
           <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', color: JERUSALEM_GOLD }}>Date:</label>
              <input type="date" id="evt-date" defaultValue={defaultDate} required style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${JERUSALEM_GOLD}`, color: '#fff', borderRadius: '4px', outline: 'none', colorScheme: 'dark' }} />
           </div>
           <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', color: JERUSALEM_GOLD }}>Start:</label>
              <input type="time" id="evt-start" defaultValue={defaultStart} required style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${JERUSALEM_GOLD}`, color: '#fff', borderRadius: '4px', outline: 'none', colorScheme: 'dark' }} />
           </div>
           <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: '8px', color: JERUSALEM_GOLD }}>End:</label>
              <input type="time" id="evt-end" defaultValue={defaultEnd} required style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.5)', border: `1px solid ${JERUSALEM_GOLD}`, color: '#fff', borderRadius: '4px', outline: 'none', colorScheme: 'dark' }} />
           </div>
        </div>
        <button type="submit" style={{ background: JERUSALEM_GOLD, color: '#000', border: 'none', padding: '15px', borderRadius: '4px', fontFamily: 'Cinzel, serif', fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px', transition: '0.2s' }}>
           {modalState.type === 'edit' ? 'Update Protocol' : 'Commit to Timeline'}
        </button>
      </form>
    );
  };

  return (
    <div style={{ 
      height: '85vh', 
      display: 'flex', 
      flexDirection: 'column', 
      padding: '40px',
      backgroundColor: 'var(--sidebar-bg)', 
      border: `1px solid ${JERUSALEM_GOLD}`,
      boxShadow: 'inset 0 0 50px rgba(0,0,0,0.5), 0 10px 30px rgba(0,0,0,0.3)',
      borderRadius: '2px'
    }}>
      
      <div style={{ 
        borderBottom: `1px solid ${JERUSALEM_GOLD}`, 
        paddingBottom: '15px', 
        marginBottom: '20px', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-end' 
      }}>
        <div>
          <h2 style={{ fontFamily: 'Cinzel, serif', color: JERUSALEM_GOLD, margin: 0, fontSize: '1.8rem', letterSpacing: '2px', textTransform: 'uppercase', textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
            The Calendar
          </h2>
          <p style={{ fontFamily: 'Courier Prime, monospace', color: VINTAGE_BEIGE, opacity: 0.7, fontSize: '0.9rem', margin: '5px 0 0 0', letterSpacing: '0.5px' }}>
            Map your reality. Defend your focus.
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '15px' }}>
          <button 
            onClick={() => setModalState({ isOpen: true, type: 'select-quest', eventData: null, selectInfo: null, prefillData: null })} 
            style={{ 
              background: 'transparent', 
              border: `1px solid ${JERUSALEM_GOLD}`, 
              color: JERUSALEM_GOLD, 
              padding: '10px 20px', 
              fontFamily: 'Cinzel, serif', 
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: '0.2s'
          }}>
            <i className="fa-solid fa-list-check" style={{ marginRight: '8px' }}></i> Schedule Quest
          </button>
          
          <button onClick={() => setModalState({ isOpen: true, type: 'add', eventData: null, selectInfo: null, prefillData: null })} 
              style={{ 
              background: 'rgba(197, 160, 89, 0.1)', 
              border: `1px solid ${JERUSALEM_GOLD}`, 
              color: JERUSALEM_GOLD, 
              padding: '10px 20px', 
              fontFamily: 'Cinzel, serif', 
              fontWeight: 'bold',
              cursor: 'pointer',
              borderRadius: '4px',
              transition: '0.2s'
          }}>
            + Allocate Time
          </button>
        </div>
      </div>

      <div className="kairos-calendar-wrapper" style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.3)', 
        border: `1px solid ${JERUSALEM_GOLD}`,
        borderTop: `4px solid ${ACCENT_RED}`,
        borderRadius: '4px',
        padding: '15px',
        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)',
        overflow: 'hidden' 
      }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek" 
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay'
          }}
          editable={true} 
          selectable={true}
          selectMirror={true} 
          dayMaxEvents={true}
          events={events}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop} 
          eventResize={handleEventResize} 
          select={handleDateSelect} 
          allDaySlot={false} 
          nowIndicator={true} 
          scrollTime="07:00:00" 
          slotDuration="00:30:00" 
          height="100%" 
        />
      </div>

      {modalState.isOpen && (
        <div 
          onClick={closeModal} 
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)',
            display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div 
            onClick={(e) => e.stopPropagation()} 
            style={{
              background: 'var(--sidebar-bg)', border: `2px solid ${JERUSALEM_GOLD}`, borderRadius: '6px',
              width: '90%', maxWidth: '550px', padding: '30px', position: 'relative',
              boxShadow: '0 15px 50px rgba(0,0,0,0.8), inset 0 0 20px rgba(0,0,0,0.5)'
          }}>
            <button onClick={closeModal} style={{ position: 'absolute', top: '15px', right: '20px', background: 'transparent', border: 'none', fontSize: '1.8rem', cursor: 'pointer', color: JERUSALEM_GOLD, transition: '0.2s' }}>
              &times;
            </button>
            <h3 style={{ margin: '0 0 25px 0', fontFamily: 'Cinzel, serif', color: JERUSALEM_GOLD, borderBottom: `1px solid ${JERUSALEM_GOLD}`, paddingBottom: '12px', textTransform: 'uppercase', letterSpacing: '1.5px', fontSize: '1.3rem' }}>
              {modalState.type === 'select-quest' ? 'Select Directive' : 
               modalState.title || (modalState.type === 'edit' ? 'Edit Ritual' : 'Initialize New Ritual')}
            </h3>
            
            {renderModalContent()}

          </div>
        </div>
      )}

    </div>
  );
};

export default Calendar;
